/**
 * Validation Module
 *
 * Provides the main validate() function and supporting utilities.
 */
import { ExerciseJSON, ValidatedExerciseJSON } from '../transformers/json-schema';
import { validateSimple } from './simple';
import { validateExpression } from './expression';
import { validateMixed } from './mixed';
import { ValidationResult } from './types';
import { VALIDATION_MODES, JSON_TYPES, INPUT_TYPES } from '../constants';
import { preprocessFractionValues } from './fraction-preprocessor';

export * from './types';
export * from './simple';
export * from './expression';
export * from './mixed';
export * from './evaluator';

/**
 * Validate user inputs against an exercise
 *
 * This is the main entry point for validation. It automatically
 * dispatches to the correct validation mode based on the exercise.
 *
 * @param exercise - The exercise JSON
 * @param userInputs - User's answers keyed by widget ID
 * @returns Validation result
 *
 * @example
 * ```typescript
 * const result = validate(exercise, { a: 10, b: 20 });
 * if (result.isCorrect) {
 *   console.log('Correct!');
 * } else {
 *   console.log('Try again');
 * }
 * ```
 */
export function validate(
  exercise: ExerciseJSON,
  userInputs: Record<string, string | number | string[][] | number[]>
): ValidationResult {
  if (!exercise.validation) {
    return {
      isCorrect: true,
      details: {}
    };
  }

  // Combine fraction sub-inputs (x__num, x__den) into "num/den" for key "x".
  // Matcher values (string[][]) pass through untouched.
  const processedInputs = preprocessFractionValues(exercise, userInputs);

  const validatedExercise = exercise as ValidatedExerciseJSON;

  switch (validatedExercise.validation.mode) {
    case VALIDATION_MODES.SIMPLE:
      return validateSimple(validatedExercise, processedInputs);

    case VALIDATION_MODES.EXPRESSION: {
      // Expression mode only uses scalar inputs; matcher values aren't referenced.
      const scalar: Record<string, string | number> = {};
      for (const [k, v] of Object.entries(processedInputs)) {
        if (typeof v === 'string' || typeof v === 'number') scalar[k] = v;
      }
      return validateExpression(validatedExercise, scalar);
    }

    case VALIDATION_MODES.MIXED:
      return validateMixed(validatedExercise, processedInputs);

    default:
      return {
        isCorrect: false,
        details: {},
        error: `Unknown validation mode: ${(exercise.validation as any).mode}`
      };
  }
}

/**
 * List every widget the exercise expects an answer for (simple answers,
 * expression variables, or both in mixed mode). Empty when the exercise has
 * no validation (display-only / hint exercises).
 *
 * @param exercise - The exercise JSON
 * @returns Array of answerable widget IDs
 */
export function getAnswerableInputs(exercise: ExerciseJSON): string[] {
  if (!exercise.validation) return [];

  const keys = new Set<string>();
  if (exercise.validation.mode === VALIDATION_MODES.SIMPLE) {
    Object.keys(exercise.validation.answers ?? {}).forEach((k) => keys.add(k));
  } else if (exercise.validation.mode === VALIDATION_MODES.EXPRESSION) {
    (exercise.validation.variables ?? []).forEach((k) => keys.add(k));
  } else if (exercise.validation.mode === VALIDATION_MODES.MIXED) {
    Object.keys(exercise.validation.answers ?? {}).forEach((k) => keys.add(k));
    (exercise.validation.variables ?? []).forEach((k) => keys.add(k));
  }
  return [...keys];
}

/**
 * Check if all required inputs are provided
 *
 * @param exercise - The exercise JSON
 * @param userInputs - User's answers
 * @returns Array of missing widget IDs
 */
export function getMissingInputs(
  exercise: ExerciseJSON,
  userInputs: Record<string, string | number | string[][] | number[]>
): string[] {
  const missing: string[] = [];

  if (!exercise.validation) {
    return missing;
  }

  // Combine fraction sub-inputs (x__num, x__den) into "x" so the answer-key
  // lookups below see the same keys validate() does. A fraction is only present
  // once BOTH parts are filled — matching the validator's notion of complete.
  const inputs = preprocessFractionValues(exercise, userInputs);

  // A widget counts as "missing" when the user supplied nothing usable:
  // undefined, null, empty string, or an empty array (matcher / visual-fraction).
  const isEmpty = (v: unknown): boolean =>
    v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

  if (exercise.validation.mode === VALIDATION_MODES.SIMPLE && exercise.validation.answers) {
    for (const widgetId of Object.keys(exercise.validation.answers)) {
      if (!(widgetId in inputs) || isEmpty(inputs[widgetId])) {
        missing.push(widgetId);
      }
    }
  } else if (exercise.validation.mode === VALIDATION_MODES.EXPRESSION && exercise.validation.variables) {
    for (const varName of exercise.validation.variables) {
      if (!(varName in inputs) || isEmpty(inputs[varName])) {
        missing.push(varName);
      }
    }
  } else if (exercise.validation.mode === VALIDATION_MODES.MIXED) {
    const keys = new Set<string>([
      ...Object.keys(exercise.validation.answers ?? {}),
      ...(exercise.validation.variables ?? []),
    ]);
    for (const k of keys) {
      if (!(k in inputs) || isEmpty(inputs[k])) missing.push(k);
    }
  }

  return missing;
}

/**
 * Check if user inputs are valid (proper format, etc.)
 * without checking correctness
 *
 * @param exercise - The exercise JSON
 * @param userInputs - User's answers
 * @returns Object with widgetId -> error message (empty if all valid)
 */
export function validateInputFormats(
  exercise: ExerciseJSON,
  userInputs: Record<string, string | number>
): Record<string, string> {
  const errors: Record<string, string> = {};

  for (const [widgetId, widget] of Object.entries(exercise.widgets)) {
    const value = userInputs[widgetId];

    if (value === undefined || value === '') {
      continue; // Missing inputs are handled by getMissingInputs
    }

    if (widget.type === JSON_TYPES.INLINE_INPUT && widget.inputType === INPUT_TYPES.NUMERIC) {
      const numValue = typeof value === 'number' ? value : parseFloat(String(value));

      if (!Number.isFinite(numValue)) {
        errors[widgetId] = 'Must be a valid number';
        continue;
      }

      // Check digit constraints
      const strValue = String(Math.abs(Math.floor(numValue)));
      if (strValue.length > widget.digits) {
        errors[widgetId] = `Maximum ${widget.digits} digits allowed`;
      }
    }
  }

  return errors;
}
